<script setup>
/**
 * @fileoverview Global Error Boundary Component
 * @description Catches and displays unhandled errors gracefully to prevent app crashes
 *
 * Features:
 * - Catches errors from child components using onErrorCaptured hook
 * - Displays user-friendly error message
 * - Shows error details in development mode
 * - Provides retry functionality (page reload)
 * - Logs errors for debugging/monitoring
 *
 * @example
 * <ErrorBoundary>
 *   <RouterView />
 * </ErrorBoundary>
 */

import { ref, onErrorCaptured } from 'vue'
import { Button } from '@/components/ui'
import { AlertTriangle, RefreshCw } from 'lucide-vue-next'

/**
 * Current error state
 * @type {import('vue').Ref<Error|null>}
 */
const error = ref(null)

/**
 * Additional error information
 * @type {import('vue').Ref<string|null>}
 */
const errorInfo = ref(null)

/**
 * Development mode flag
 * @type {boolean}
 */
const isDevelopment = import.meta.env.DEV

/**
 * Error capture hook
 * Prevents error propagation and stores error details
 */
onErrorCaptured((err, instance, info) => {
  error.value = err
  errorInfo.value = info

  // Log to console for debugging
  console.error('ErrorBoundary caught:', {
    error: err,
    errorInfo: info,
    component: instance?.$options?.name || 'Unknown',
    timestamp: new Date().toISOString()
  })

  // TODO: Send to error tracking service (e.g., Sentry) in production
  // if (import.meta.env.PROD) {
  //   sendToErrorTracking(err, info)
  // }

  // Return false to prevent error from propagating further
  return false
})

/**
 * Handle retry action
 * Clears error state and reloads the page
 */
function handleRetry() {
  error.value = null
  errorInfo.value = null
  // Force re-render by reloading the page
  window.location.reload()
}
</script>

<template>
  <!-- Error State -->
  <div
    v-if="error"
    class="min-h-screen flex items-center justify-center p-4 bg-slate-50"
    role="alert"
    aria-live="assertive"
  >
    <div class="text-center max-w-md">
      <!-- Error Icon -->
      <AlertTriangle class="h-16 w-16 text-destructive mx-auto mb-4" aria-hidden="true" />

      <!-- Error Message -->
      <h1 class="text-2xl font-bold text-slate-900 mb-2">Something went wrong</h1>
      <p class="text-muted-foreground mb-6">
        An unexpected error occurred. Please try refreshing the page.
      </p>

      <!-- Development Error Details -->
      <div
        v-if="isDevelopment"
        class="bg-slate-100 border border-slate-200 p-4 rounded-lg text-left mb-6 overflow-auto max-h-64"
      >
        <p class="text-xs font-mono text-slate-600 mb-2">Development Error Details:</p>
        <code class="text-xs font-mono text-destructive block mb-2">
          {{ error.message }}
        </code>
        <details class="text-xs font-mono text-slate-600">
          <summary class="cursor-pointer hover:text-slate-900">Stack Trace</summary>
          <pre class="mt-2 whitespace-pre-wrap">{{ error.stack }}</pre>
        </details>
        <p v-if="errorInfo" class="text-xs font-mono text-slate-600 mt-2">
          Error Info: {{ errorInfo }}
        </p>
      </div>

      <!-- Retry Button -->
      <Button @click="handleRetry" size="lg">
        <RefreshCw class="h-4 w-4 mr-2" aria-hidden="true" />
        Reload Page
      </Button>
    </div>
  </div>

  <!-- Normal Content (no error) -->
  <slot v-else />
</template>
